/**
 * Key chords are stored as "Ctrl+Alt+Shift+Meta+<KeyboardEvent.code>" with the
 * modifiers in that fixed order, so the same physical key works on every
 * keyboard layout and two spellings of one chord can never differ.
 */

const MODIFIERS = ['Ctrl', 'Alt', 'Shift', 'Meta'] as const;
type Modifier = (typeof MODIFIERS)[number];

const MODIFIER_CODES = new Set([
  'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'ShiftLeft', 'ShiftRight', 'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight',
  'CapsLock', 'NumLock', 'ScrollLock', 'Fn', 'FnLock',
]);

const MODIFIER_ALIASES: Record<string, Modifier> = {
  ctrl: 'Ctrl', control: 'Ctrl', alt: 'Alt', option: 'Alt', shift: 'Shift', meta: 'Meta', cmd: 'Meta', command: 'Meta', win: 'Meta',
};

export interface Chord {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
  code: string;
}

export interface KeyLike {
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
}

export function chordToString(chord: Chord): string {
  const parts: string[] = [];
  if (chord.ctrl) parts.push('Ctrl');
  if (chord.alt) parts.push('Alt');
  if (chord.shift) parts.push('Shift');
  if (chord.meta) parts.push('Meta');
  parts.push(chord.code);
  return parts.join('+');
}

/** Parses a stored chord; null for anything that is not "modifiers + one key code". */
export function parseChord(text: string): Chord | null {
  const parts = text.split('+').map((p) => p.trim());
  if (parts.some((p) => p === '')) return null;
  const chord: Chord = { ctrl: false, alt: false, shift: false, meta: false, code: '' };
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const modifier = MODIFIER_ALIASES[part.toLowerCase()];
    if (i < parts.length - 1) {
      if (!modifier) return null;
      const key = modifier.toLowerCase() as 'ctrl' | 'alt' | 'shift' | 'meta';
      if (chord[key]) return null;
      chord[key] = true;
    } else {
      if (modifier || MODIFIER_CODES.has(part) || !/^[A-Za-z][A-Za-z0-9]*$/.test(part)) return null;
      chord.code = part;
    }
  }
  return chord;
}

/** Canonical spelling of a chord string, or null if it is not a valid chord. */
export function normalizeChord(text: string): string | null {
  const chord = parseChord(text);
  return chord ? chordToString(chord) : null;
}

/** The chord a key event represents, or null while only modifiers are held. */
export function chordFromEvent(event: KeyLike): string | null {
  if (!event.code || MODIFIER_CODES.has(event.code)) return null;
  return chordToString({ ctrl: event.ctrlKey, alt: event.altKey, shift: event.shiftKey, meta: event.metaKey, code: event.code });
}

export function matchesChord(event: KeyLike, chord: string): boolean {
  const pressed = chordFromEvent(event);
  return pressed !== null && pressed === normalizeChord(chord);
}

const CODE_LABELS: Record<string, string> = {
  ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓', Escape: 'Esc', Delete: 'Del', Backspace: '⌫', Enter: 'Enter',
  Space: 'Space', Minus: '-', Equal: '=', Comma: ',', Period: '.', Slash: '/', Backslash: '\\', Semicolon: ';', Quote: "'",
  BracketLeft: '[', BracketRight: ']', Backquote: '`',
};

/** Human-readable form for menus and the hotkey editor ("Ctrl+Shift+R"). */
export function formatChord(chord: string, mac = false): string {
  const parsed = parseChord(chord);
  if (!parsed) return chord;
  const parts: string[] = [];
  if (parsed.ctrl) parts.push(mac ? '⌃' : 'Ctrl');
  if (parsed.alt) parts.push(mac ? '⌥' : 'Alt');
  if (parsed.shift) parts.push(mac ? '⇧' : 'Shift');
  if (parsed.meta) parts.push(mac ? '⌘' : 'Meta');
  const { code } = parsed;
  let key = CODE_LABELS[code] ?? code;
  if (/^Key[A-Z]$/.test(code)) key = code.slice(3);
  else if (/^Digit[0-9]$/.test(code)) key = code.slice(5);
  else if (/^Numpad/.test(code)) key = `Num ${code.slice(6)}`;
  parts.push(key);
  return parts.join(mac ? '' : '+');
}

/**
 * Combinations the browser or OS keeps for itself (the page never sees them, or
 * hijacking them would trap the user). Refused in the hotkey editor by default.
 */
const RESERVED = new Set(
  [
    'Ctrl+KeyT', 'Ctrl+KeyW', 'Ctrl+KeyN', 'Ctrl+Shift+KeyT', 'Ctrl+Shift+KeyN', 'Ctrl+Shift+KeyW', 'Ctrl+Tab', 'Ctrl+Shift+Tab',
    'Ctrl+KeyR', 'Ctrl+Shift+KeyR', 'Ctrl+KeyL', 'Ctrl+KeyQ', 'Ctrl+Shift+KeyI', 'Ctrl+Shift+KeyJ', 'Ctrl+KeyP',
    'Meta+KeyT', 'Meta+KeyW', 'Meta+KeyN', 'Meta+Shift+KeyT', 'Meta+Shift+KeyN', 'Meta+KeyR', 'Meta+Shift+KeyR', 'Meta+KeyL',
    'Meta+KeyQ', 'Meta+KeyH', 'Meta+KeyM', 'Meta+Tab', 'Meta+Space', 'Alt+Meta+KeyI', 'Meta+KeyP',
    'Alt+F4', 'Alt+Tab', 'F5', 'Ctrl+F5', 'F11', 'F12', 'Tab', 'Shift+Tab',
  ].map((c) => normalizeChord(c)!),
);

export function isReservedChord(chord: string): boolean {
  const normal = normalizeChord(chord);
  return normal !== null && RESERVED.has(normal);
}

export interface BindableAction {
  id: string;
  label: string;
  defaults?: readonly string[];
}

/**
 * Effective chords per action: the project's binding when it has one,
 * otherwise the action's defaults. A project entry holding only '' means
 * "explicitly unbound" (an empty array cannot be stored: setHotkey deletes it).
 */
export function resolveBindings(
  actions: readonly BindableAction[],
  stored: Readonly<Record<string, readonly string[]>>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const action of actions) {
    const custom = stored[action.id];
    const source = custom ?? action.defaults ?? [];
    const seen = new Set<string>();
    for (const chord of source) {
      const normal = normalizeChord(chord);
      if (normal) seen.add(normal);
    }
    out[action.id] = [...seen];
  }
  return out;
}

/** Marker stored for an action whose default binding the user removed. */
export const UNBOUND = '';

/** Chords bound to more than one action, mapped to the action ids sharing them. */
export function findConflicts(bindings: Readonly<Record<string, readonly string[]>>): Record<string, string[]> {
  const owners = new Map<string, string[]>();
  for (const [actionId, chords] of Object.entries(bindings)) {
    for (const chord of chords) {
      const list = owners.get(chord);
      if (list) {
        if (!list.includes(actionId)) list.push(actionId);
      } else owners.set(chord, [actionId]);
    }
  }
  const out: Record<string, string[]> = {};
  for (const [chord, list] of owners) if (list.length > 1) out[chord] = list;
  return out;
}

/** First action bound to the pressed chord, in action order. */
export function findAction(
  event: KeyLike,
  bindings: Readonly<Record<string, readonly string[]>>,
  order: readonly string[],
): string | null {
  const pressed = chordFromEvent(event);
  if (!pressed) return null;
  for (const id of order) if (bindings[id]?.includes(pressed)) return id;
  return null;
}

/** True when the event comes from somewhere the user is typing. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  const element = target as HTMLElement;
  if (element.isContentEditable) return true;
  return element.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]') !== null;
}
